import * as Sentry from '@sentry/react'

// A frame is ours only if it comes from a script file we served: an http(s) URL
// whose path ends in a script extension, query string and fragment allowed.
// Built assets are .js; the extensions past that are for the dev server, which
// serves modules under their source names (e.g. /vite-dev/pages/foo.tsx?t=123).
// Anything else — a pseudo-scheme, or the document URL itself — is injected
// third-party code.
function isOwnScript(filename: string): boolean {
  return /^https?:\/\/[^?#]*\.[cm]?[jt]sx?(\?|#|$)/.test(filename)
}

Sentry.init({
  dsn: 'https://b932455b454a6ba69030da2ff37bbed3@o4510482057592832.ingest.de.sentry.io/4510482058969168',
  integrations: [
    Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
  ],
  environment: import.meta.env.MODE,
  ignoreErrors: [
    // Instagram/Facebook in-app browser injects its own script that talks to its
    // native host via window.webkit.messageHandlers (iOS); that handler isn't
    // always registered, causing errors unrelated to this app.
    /webkit\.messageHandlers/,
    // Mobile Safari's own internal autofill/QuickType script throws this;
    // it has no filename/line info, so it's not one of our frames and can't
    // be caused by our code.
    "Can't find variable: EmptyRanges",
  ],
  // Every line of our own client code is served from a script file
  // (vite_typescript_tag renders <script src=".../vite/assets/*.js">), so a
  // throw site that isn't one is somebody else's code running in the page.
  // Two flavours show up in practice:
  //
  //   * In-app browsers (Facebook, Instagram, WeChat, Line, ...) inject bridge
  //     scripts that report under a pseudo-URL scheme of their own
  //     (e.g. iabjs://navigation_performance_logger_android).
  //   * Scripts injected into a WKWebView (in-app browser instrumentation,
  //     in-page translators) carry no source URL, so WebKit attributes them to
  //     the *document* URL — they arrive looking like a frame in the page the
  //     candidate was on, e.g. a "Maximum call stack size exceeded." from a
  //     recursive DOM walker with every frame pointing at /:org_slug/:event.
  //
  // Matching on the filename covers both, and any future variation, instead of
  // chasing each injected script's error strings one at a time.
  //
  // The only JavaScript we serve inline in the document is the theme setter in
  // the layout <head> (which runs before Sentry is even loaded) and the
  // window.I18nTranslations assignment, neither of which can throw here, so
  // nothing of ours is lost by treating document-attributed frames as foreign.
  beforeSend(event) {
    // Frames are ordered oldest → newest, so the last one is where the
    // exception actually threw. Only drop it if the throw site itself is
    // foreign code — a real bug in our app that merely calls into a bridge
    // still throws (and reports) from our own asset frame.
    const values = event.exception?.values ?? []
    const frames = values[values.length - 1]?.stacktrace?.frames
    const throwSite = frames?.[frames.length - 1]
    if (throwSite?.filename && !isOwnScript(throwSite.filename)) return null
    return event
  },
  beforeBreadcrumb(breadcrumb, hint) {
    if (breadcrumb.category?.startsWith('ui.') && hint?.event) {
      const target = (hint.event as Event).target as Element | null
      if (!target) return breadcrumb

      // Walk up to the nearest interactive element so clicking an icon inside
      // a button still resolves to the button's label
      const el = target.closest('button, a, [role="button"], [role="link"]') ?? target

      // If inside a label that wraps a checkbox/radio, report the field name + checked state
      const wrappingLabel = el.tagName === 'LABEL' ? el : el.closest('label')
      const associatedCheckbox = wrappingLabel?.querySelector<HTMLInputElement>('input[type="checkbox"], input[type="radio"]')
      if (associatedCheckbox) {
        const fieldName = associatedCheckbox.getAttribute('name')
          ?.replace(/\[\]$/, '')
          ?.replace(/^.*\[(.+)\]$/, '$1') ?? 'checkbox'
        breadcrumb.message = `${fieldName}: ${associatedCheckbox.checked ? 'selected' : 'deselected'}`
        return breadcrumb
      }

      const isFormField = el.matches('input, textarea, select')

      const associatedLabel = el.id
        ? document.querySelector<HTMLLabelElement>(`label[for="${el.id}"]`)?.textContent?.trim()
        : null

      // Strip Rails param wrapper, also handle array fields e.g. "candidate[role_ids][]" → "role_ids"
      const cleanName = el.getAttribute('name')
        ?.replace(/\[\]$/, '')
        ?.replace(/^.*\[(.+)\]$/, '$1') ?? null

      const label =
        el.getAttribute('aria-label') ||
        el.closest('[data-label]')?.getAttribute('data-label') ||
        associatedLabel ||
        (!isFormField ? el.textContent?.trim().replace(/\s+/g, ' ').slice(0, 60) : null) ||
        el.getAttribute('placeholder') ||
        cleanName ||
        el.id ||
        el.tagName.toLowerCase()

      if (label) breadcrumb.message = label
    }
    return breadcrumb
  },
})